import { FormLabel, Grid, Radio, RadioGroup, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { TenantRole } from "@nens/rana/api-v1";
import { RanaLoading, RanaModal } from "@nens/ranalib";
import { RanaForm } from "@nens/ranalib";

import { cancelButtonText, userRoleDescriptionString, userRoleString } from "@/translations";

import { useTenantUserInviteModal } from "../hooks/use-tenant-user-invite-modal";

interface TenantUserInviteModalProps {
  onClose: () => void;
}

export function TenantUserInviteModal({ onClose }: TenantUserInviteModalProps) {
  const intl = useIntl();

  const {
    searchString,
    setSearchString,
    role,
    setRole,
    inviteUser,
    showInviteButton,
    emailExists,
    isLoading,
  } = useTenantUserInviteModal(onClose);

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={
        <FormattedMessage
          id="header.invite_user_to_organisation"
          defaultMessage="Invite a user to the organisation"
        />
      }
      w={{ base: "full", md: "2xl" }}
      minW={0}
      h={"xl"}
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          onClick: onClose,
          isDisabled: isLoading,
        },
        {
          children: intl.formatMessage({
            id: "button.invite_user",
            defaultMessage: "Invite user",
          }),
          variant: "primary",
          onClick: inviteUser,
          isDisabled: !showInviteButton,
        },
      ]}
    >
      <RanaForm>
        <RanaForm.Input
          field="email"
          type="email"
          label={intl.formatMessage({
            id: "label.email",
            defaultMessage: "Email address",
          })}
          explanation={intl.formatMessage({
            id: "input.email_address",
            defaultMessage: "Enter email address",
          })}
          value={searchString}
          onChange={(e) => setSearchString(e.target.value)}
          autoFocus
          mb={4}
        />
        <RadioGroup display="flex" flexDir="column" gap={2} mb={2} value={role}>
          <FormLabel>
            <FormattedMessage id="label.select_role" defaultMessage="Select role of the user" />
          </FormLabel>
          {Object.values(TenantRole)
            .filter((role) => role !== TenantRole.Guest)
            .map((newRole) => (
              <Grid
                alignItems="center"
                templateColumns="20px 100px 1fr"
                borderWidth="base"
                borderColor="brand.grey.200"
                rounded="base"
                bgColor={newRole === role ? "brand.cloudy" : "brand.bg"}
                fontSize={14}
                fontWeight={400}
                gap={4}
                p={4}
                _hover={{ bgColor: "gray.50" }}
                onClick={() => setRole(newRole)}
                cursor="pointer"
                key={newRole}
              >
                <Radio value={newRole} />
                <Text>{userRoleString(intl, newRole)}</Text>
                <Text color="brand.grey.600" overflow="hidden">
                  {userRoleDescriptionString(intl, newRole)}
                </Text>
              </Grid>
            ))}
        </RadioGroup>
        <RanaForm.Errors />
        {emailExists && (
          <Text color="brand.accent">
            <FormattedMessage
              id="errors.user_already_exists"
              defaultMessage="User with email {email} already exists in the organisation."
              values={{ email: searchString }}
            />
          </Text>
        )}
        {isLoading && <RanaLoading size="lg" mt={4} isCentered />}
      </RanaForm>
    </RanaModal>
  );
}
